// ─── Import / Export d'inventaires ────────────────────────────────────────
// Deux objets exportables :
//   • « modele »  : structure seule (arbre de catégories + propriétés), sans
//                   données ni identifiants. Ré-instanciable comme un modèle.
//   • « complet » : structure + objets (+ photos en base64), à venir.
//
// Format fichier (JSON) :
//   { mopcalInventaire: 1, type:'modele'|'complet', app, version, nom, emoji,
//     arbre:[{nom,emoji,enfants}], proprietes:[{nom,type,options?}], ... }
//
// I/O fichier : web = download / <input file> ; mobile = expo-file-system
// (écriture cache) + expo-sharing (export) et expo-document-picker (import).
import { Platform } from 'react-native';
import { APP_VERSION } from '@mop/core';
import { nouvelId } from './inventaires';

export const MOPCAL_INV_FORMAT = 1;

// ── Construction de l'export « modèle » (structure pure, sans id ni photo) ──
function noeudPur(n) {
  return { nom: n?.nom || '', emoji: n?.emoji || '📁', enfants: (n?.enfants || []).map(noeudPur) };
}
function propPure(p) {
  return { nom: p?.nom || '', type: p?.type || 'texte', ...(p?.options ? { options: p.options.slice() } : {}) };
}

export function construireExportModele(inv) {
  return {
    mopcalInventaire: MOPCAL_INV_FORMAT,
    type:    'modele',
    app:     'MoPCal',
    version: APP_VERSION,
    nom:     inv?.nom || '',
    emoji:   inv?.emoji || '📦',
    arbre:        (inv?.arbre || []).map(noeudPur),
    proprietes:   (inv?.proprietes || []).map(propPure),
    emplacements: emplacementsPourExport(inv?.emplacements),
  };
}

// ── Construction de l'export « complet » (structure + objets) ──────────────
// On CONSERVE les id (arbre + propriétés) pour préserver les liens objet→catId
// et vals→propId ; ils seront ré-indexés à l'import. `avecPhotos` : inclut les
// photos (base64) — géré à l'étape 3 ; ici on peut les exclure.
function itemPur(it, avecPhotos) {
  const o = {
    nom:         it?.nom || '',
    catId:       it?.catId != null ? it.catId : null,
    qte:         Number(it?.qte) || 0,
    seuil:       Number(it?.seuil) || 0,
    description: it?.description || '',
    vals:        (it?.vals && typeof it.vals === 'object') ? it.vals : {},
    ...(it?.codeBarres ? { codeBarres: it.codeBarres } : {}),
    ...(it?.unique ? { unique: true } : {}),
  };
  if (Array.isArray(it?.articles) && it.articles.length) {
    o.articles = it.articles.map(a => ({
      nom: a?.nom || '', qte: Number(a?.qte) || 0, vals: (a?.vals && typeof a.vals === 'object') ? a.vals : {},
      ...(a?.seuil ? { seuil: Number(a.seuil) } : {}),
      ...(a?.codeBarres ? { codeBarres: a.codeBarres } : {}),
      ...(a?.unique ? { unique: true } : {}),
      ...(a?.consommable ? { consommable: true } : {}),
      ...(a?.emplacementId ? { emplacementId: a.emplacementId } : {}),
      ...(avecPhotos && a?.photo ? { photo: a.photo } : {}),
    }));
  }
  if (avecPhotos && Array.isArray(it?.photos) && it.photos.length) o.photos = it.photos;
  return o;
}

// Copie de l'arbre en conservant les id (liens objets) ; retire la photo de
// catégorie si on n'exporte pas les photos.
function arbrePourExport(arbre, avecPhotos) {
  return (arbre || []).map(n => ({
    id: n?.id, nom: n?.nom || '', emoji: n?.emoji || '📁',
    ...(avecPhotos && n?.photo ? { photo: n.photo } : {}),
    enfants: arbrePourExport(n?.enfants, avecPhotos),
  }));
}

// Arbre des lieux (emplacements) — AVEC id (les articles y font référence).
function emplacementsPourExport(emps) {
  return (emps || []).map(n => ({
    id: n?.id, nom: n?.nom || '', emoji: n?.emoji || '📍',
    ...(n?.lieuId ? { lieuId: n.lieuId } : {}),   // lien vers un lieu du calendrier
    ...(n?.planCols ? { planCols: n.planCols } : {}),   // plan 2D : colonnes
    ...(n?.planRows ? { planRows: n.planRows } : {}),   // plan 2D : lignes
    ...(Array.isArray(n?.planMurs) && n.planMurs.length ? { planMurs: n.planMurs } : {}), // murs/portes/fenêtres
    ...(n?.murV ? { murV: n.murV } : {}),               // version du schéma des murs (coordonnées grille)
    ...(n?.cell ? { cell: n.cell } : {}),               // rectangle englobant / position dans le plan du parent
    ...(Array.isArray(n?.cells) && n.cells.length ? { cells: n.cells } : {}), // zone non rectangulaire (forme libre)
    enfants: emplacementsPourExport(n?.enfants),
  }));
}

export function construireExportComplet(inv, items, { avecPhotos = false } = {}) {
  return {
    mopcalInventaire: MOPCAL_INV_FORMAT,
    type:    'complet',
    app:     'MoPCal',
    version: APP_VERSION,
    avecPhotos: !!avecPhotos,
    nom:     inv?.nom || '',
    emoji:   inv?.emoji || '📦',
    arbre:        arbrePourExport(inv?.arbre, avecPhotos),   // AVEC id (liens objets)
    proprietes:   inv?.proprietes || [],                     // AVEC id (liens vals)
    emplacements: emplacementsPourExport(inv?.emplacements), // AVEC id (liens articles)
    items:        (items || []).map(it => itemPur(it, avecPhotos)),
  };
}

// ── Ré-indexation à l'import (id neufs + maps oldId→newId) ─────────────────
function reindexerArbre(arbre) {
  const map = {};
  const walk = (list) => (list || []).map(n => {
    const nid = nouvelId('cat');
    if (n && n.id != null) map[n.id] = nid;
    return { id: nid, nom: (n && n.nom) || '', emoji: (n && n.emoji) || '📁', ...(n && n.photo ? { photo: n.photo } : {}), enfants: walk(n && n.enfants) };
  });
  return { arbre: walk(arbre), map };
}
function reindexerEmplacements(emps) {
  const map = {};
  const walk = (list) => (list || []).map(n => {
    const nid = nouvelId('emp');
    if (n && n.id != null) map[n.id] = nid;
    // lieuId pointe vers un lieu du CALENDRIER (pas un id d'inventaire) → conservé tel quel.
    // Le plan 2D (planCols/planRows/planMurs/cell) référence des coordonnées, pas des id → conservé.
    return {
      id: nid, nom: (n && n.nom) || '', emoji: (n && n.emoji) || '📍',
      ...(n && n.lieuId ? { lieuId: n.lieuId } : {}),
      ...(n && n.planCols ? { planCols: n.planCols } : {}),
      ...(n && n.planRows ? { planRows: n.planRows } : {}),
      ...(n && Array.isArray(n.planMurs) && n.planMurs.length ? { planMurs: n.planMurs } : {}),
      ...(n && n.murV ? { murV: n.murV } : {}),
      ...(n && n.cell ? { cell: n.cell } : {}),
      ...(n && Array.isArray(n.cells) && n.cells.length ? { cells: n.cells } : {}),
      enfants: walk(n && n.enfants),
    };
  });
  return { emplacements: walk(emps), map };
}
function reindexerProps(proprietes) {
  const map = {};
  const props = (proprietes || []).map(p => {
    const nid = nouvelId('prop');
    if (p && p.id != null) map[p.id] = nid;
    return { id: nid, nom: (p && p.nom) || '', type: (p && p.type) || 'texte', ...(p && p.options ? { options: p.options.slice() } : {}) };
  });
  return { proprietes: props, map };
}
function remapVals(vals, propMap) {
  const out = {};
  for (const k of Object.keys(vals || {})) out[propMap[k] || k] = vals[k];
  return out;
}

// Fichier « complet » → { nom, emoji, arbre, proprietes, items } prêts à créer.
// Les catId/propId des objets sont remappés vers les nouveaux id de la structure.
export function preparerImportComplet(parsed) {
  const { arbre, map: catMap } = reindexerArbre(parsed && parsed.arbre);
  const { proprietes, map: propMap } = reindexerProps(parsed && parsed.proprietes);
  const { emplacements, map: empMap } = reindexerEmplacements(parsed && parsed.emplacements);
  const remapEmp = (id) => (id != null && empMap[id]) ? empMap[id] : null;
  const items = ((parsed && parsed.items) || []).map(it => ({
    nom:         it && it.nom || '',
    catId:       (it && it.catId != null && catMap[it.catId]) ? catMap[it.catId] : null,
    qte:         Number(it && it.qte) || 0,
    seuil:       Number(it && it.seuil) || 0,
    description: (it && it.description) || '',
    ...(it && it.codeBarres ? { codeBarres: it.codeBarres } : {}),
    ...(it && it.unique ? { unique: true } : {}),
    vals:        remapVals(it && it.vals, propMap),
    ...(Array.isArray(it && it.articles) && it.articles.length ? {
      articles: it.articles.map(a => ({ id: nouvelId('art'), nom: (a && a.nom) || '', qte: Number(a && a.qte) || 0, ...(a && a.seuil ? { seuil: Number(a.seuil) } : {}), vals: remapVals(a && a.vals, propMap), ...(a && a.codeBarres ? { codeBarres: a.codeBarres } : {}), ...(a && a.unique ? { unique: true } : {}), ...(a && a.consommable ? { consommable: true } : {}), ...(remapEmp(a && a.emplacementId) ? { emplacementId: remapEmp(a.emplacementId) } : {}), ...(a && a.photo ? { photo: a.photo } : {}) })),
    } : {}),
    ...(Array.isArray(it && it.photos) && it.photos.length ? { photos: it.photos } : {}),
  }));
  return { nom: (parsed && (parsed.nom || parsed.titre)) || '', emoji: (parsed && parsed.emoji) || '📦', arbre, proprietes, emplacements, items };
}

// ── Photos : conversion URL Storage ↔ base64 embarqué ─────────────────────
function blobEnDataURL(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}
async function urlEnBase64(url) {
  try { const r = await fetch(url); const b = await r.blob(); return await blobEnDataURL(b); }
  catch (_) { return null; }
}
// Une photo { url, chemin } → { b64 } ; renvoie null si la conversion échoue.
async function photoEnBase64(photo) {
  const url = photo && photo.url;
  if (!url) return null;
  const b64 = await urlEnBase64(url);
  return b64 ? { b64 } : null;
}

// Export : remplace chaque photo (url) par sa version base64 embarquée. Mute et
// renvoie l'objet d'export. Les photos non converties (erreur réseau) sont retirées.
export async function embarquerPhotosBase64(obj) {
  const walkArbre = async (list) => {
    for (const n of (list || [])) {
      if (n && n.photo) { const p = await photoEnBase64(n.photo); if (p) n.photo = p; else delete n.photo; }
      await walkArbre(n && n.enfants);
    }
  };
  await walkArbre(obj && obj.arbre);
  for (const it of ((obj && obj.items) || [])) {
    if (Array.isArray(it.photos)) {
      const out = [];
      for (const ph of it.photos) { const p = await photoEnBase64(ph); if (p) out.push(p); }
      it.photos = out;
    }
    if (Array.isArray(it.articles)) {
      for (const a of it.articles) { if (a && a.photo) { const p = await photoEnBase64(a.photo); if (p) a.photo = p; else delete a.photo; } }
    }
  }
  if (obj) obj.avecPhotos = true;
  return obj;
}

// Import : ré-upload chaque photo base64 (b64) via `uploader(dataUri) → {url,chemin}`.
// Mute et renvoie `prep` (arbre + items) avec des photos matérialisées dans le Storage cible.
export async function materialiserPhotos(prep, uploader) {
  const walkArbre = async (list) => {
    for (const n of (list || [])) {
      if (n && n.photo && n.photo.b64) { try { n.photo = await uploader(n.photo.b64); } catch (_) { delete n.photo; } }
      await walkArbre(n && n.enfants);
    }
  };
  await walkArbre(prep && prep.arbre);
  for (const it of ((prep && prep.items) || [])) {
    if (Array.isArray(it.photos) && it.photos.length) {
      const out = [];
      for (const ph of it.photos) {
        if (ph && ph.b64) { try { out.push(await uploader(ph.b64)); } catch (_) {} }
        else if (ph && ph.url) out.push(ph);
      }
      it.photos = out;
    }
    if (Array.isArray(it.articles)) {
      for (const a of it.articles) { if (a && a.photo && a.photo.b64) { try { a.photo = await uploader(a.photo.b64); } catch (_) { delete a.photo; } } }
    }
  }
  return prep;
}

// Un fichier importé (modèle OU complet) → structure prête pour instancierModele.
export function modeleDepuisImport(parsed) {
  return {
    nom:        parsed?.nom || parsed?.titre || '',
    emoji:      parsed?.emoji || '📦',
    arbre:      Array.isArray(parsed?.arbre) ? parsed.arbre : [],
    proprietes: Array.isArray(parsed?.proprietes) ? parsed.proprietes : [],
    emplacements: Array.isArray(parsed?.emplacements) ? parsed.emplacements : [],
  };
}

// Nom de fichier « sûr » (sans accents ni caractères spéciaux).
export function nomFichierSafe(nom, suffixe) {
  const base = String(nom || 'inventaire')
    .replace(/[^a-zA-Z0-9-_ ]/g, '').trim().replace(/\s+/g, '-').slice(0, 40) || 'inventaire';
  return `${base}.${suffixe}.json`;
}

// ── Écriture / partage du fichier ─────────────────────────────────────────
// Web : téléchargement. Mobile : écriture dans le cache + feuille de partage.
export async function exporterFichier({ nomFichier, contenu }) {
  if (Platform.OS === 'web') {
    const blob = new Blob([contenu], { type: 'application/json;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = nomFichier;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return { ok: true, methode: 'download' };
  }
  const L = require('expo-file-system/legacy');
  const Sharing = require('expo-sharing');
  const uri = (L.cacheDirectory || '') + nomFichier;
  await L.writeAsStringAsync(uri, contenu, { encoding: (L.EncodingType && L.EncodingType.UTF8) || 'utf8' });
  if (await Sharing.isAvailableAsync()) {
    await Sharing.shareAsync(uri, { mimeType: 'application/json', dialogTitle: nomFichier, UTI: 'public.json' });
  }
  return { ok: true, methode: 'share' };
}

// ── Sélection + lecture d'un fichier d'inventaire ─────────────────────────
// Renvoie l'objet JSON parsé, ou null si annulé. Lève 'FICHIER_INVALIDE' si le
// contenu n'est pas un export MoPCal valide.
function choisirFichierWeb() {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json,.json';
    input.onchange = () => resolve(input.files && input.files[0] ? input.files[0] : null);
    input.click();
  });
}

export async function lireFichierInventaire() {
  let texte = null;
  if (Platform.OS === 'web') {
    const f = await choisirFichierWeb();
    if (!f) return null;
    texte = await f.text();
  } else {
    const DocumentPicker = require('expo-document-picker');
    const res = await DocumentPicker.getDocumentAsync({ copyToCacheDirectory: true, type: '*/*' });
    const a = res.assets && res.assets[0];
    if (res.canceled || !a || !a.uri) return null;
    const FS = require('expo-file-system');
    if (FS && FS.File) { texte = await new FS.File(a.uri).text(); }
    else { const L = require('expo-file-system/legacy'); texte = await L.readAsStringAsync(a.uri); }
  }
  if (texte == null) return null;
  let obj;
  try { obj = JSON.parse(texte); } catch (_) { throw new Error('FICHIER_INVALIDE'); }
  if (!obj || obj.mopcalInventaire == null) throw new Error('FICHIER_INVALIDE');
  return obj;
}
